import {
  BookingAddBeautyInput,
  BookingCancelBeautyInput,
  BookingServiceItem,
  BookingTable,
  BookingType,
} from "@okeys/shared-utils"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

export interface CreateBookingForPaymentInput {
  bookingType: BookingType
  services: BookingServiceItem[]
  clientId?: string
  prestatereId?: string
  scheduledStart?: string
  scheduledEnd?: string
  notes?: string
}

export interface CreateBookingForPaymentOutput {
  bookingId: string
  booking: BookingTable
}

export const useBookingQuery = () => {
  const { data, isPending, isError, isSuccess, refetch, isFetching, error } =
    useQuery<BookingTable[]>({
      queryKey: ["booking"],
      queryFn: async () => {
        const token = await getToken()
        const attrs = await fetchUserAttributes()
        const shopId = attrs["custom:shopId"]

        // Ne pas filtrer par jour pour récupérer tous les bookings
        console.log("🔍 [bookingQuery] Fetching bookings for shopId:", shopId)

        const url = shopId
          ? `/booking?shopId=${encodeURIComponent(shopId)}`
          : "/booking"

        console.log("🔍 [bookingQuery] Request URL:", url)

        const response = await customAxios.get(url, {
          headers: {
            Authorization: token,
          },
        })
        const payload = response.data
        if (Array.isArray(payload)) return payload as BookingTable[]
        if (payload?.data?.bookings)
          return payload.data.bookings as BookingTable[]
        if (payload?.bookings) return payload.bookings as BookingTable[]
        return []
      },
    })

  return {
    pendingBooking: isPending,
    errorBooking: isError,
    errorBookingMessage: error,
    successBooking: isSuccess,
    refetchBooking: refetch,
    booking: data,
    fetchingBooking: isFetching,
  }
}

export const useBookingAddMutation = () => {
  const queryClient = useQueryClient()

  const bookingAdd = async (bookingData: BookingAddBeautyInput) => {
    const token = await getToken()
    if (!token) {
      throw new Error("No token found")
    }

    const response = await customAxios.put("/booking", bookingData, {
      headers: {
        Authorization: token,
      },
    })
    return response.data
  }

  const mutation = useMutation({
    mutationFn: bookingAdd,
    onMutate: async (bookingData) => {
      await queryClient.cancelQueries({ queryKey: ["booking"] })
      const previousBookings = queryClient.getQueryData<BookingTable[]>([
        "booking",
      ])

      // Optimistic update - partial data for UI, real data comes from server
      const tempBooking = {
        ...bookingData,
        bookingId: `temp-${Date.now()}`,
        bookingType: "booking",
        state: "pending",
        createdAt: new Date().toISOString(),
        lastUpdateAt: new Date().toISOString(),
        lastUpdateByType: "manager",
        lastUpdateById: "manager",
        timezone: "Europe/Paris",
        localDay: bookingData.scheduledStart.split("T")[0],
        version: 1,
      } as unknown as BookingTable

      queryClient.setQueryData<BookingTable[]>(["booking"], (old) => {
        if (!old) return [tempBooking]
        return [tempBooking, ...old]
      })

      return { previousBookings }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousBookings) {
        queryClient.setQueryData(["booking"], context.previousBookings)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["booking"] })
    },
  })

  return mutation
}

export const useBookingCancellationMutation = () => {
  const queryClient = useQueryClient()

  const bookingCancellation = async ({
    bookingId,
    message,
  }: BookingCancelBeautyInput) => {
    const token = await getToken()
    if (!token) throw new Error("No token found")
    const ret = await customAxios.put(
      "/booking/cancelation",
      { bookingId, message: message || "" },
      { headers: { Authorization: token } }
    )
    return ret.data
  }

  return useMutation({
    mutationFn: bookingCancellation,
    onMutate: async ({ bookingId }) => {
      await queryClient.cancelQueries({ queryKey: ["booking"] })

      const previousBookings = queryClient.getQueryData<BookingTable[]>([
        "booking",
      ])

      queryClient.setQueryData<BookingTable[]>(["booking"], (old) => {
        if (!old) return []
        return old.filter((b) => b.bookingId !== bookingId)
      })

      return { previousBookings }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousBookings) {
        queryClient.setQueryData(["booking"], ctx.previousBookings)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["booking"] })
    },
  })
}

export const useBookingValidationMutation = () => {
  const queryClient = useQueryClient()

  const bookingValidation = async ({
    bookingIds,
  }: {
    bookingIds: string[]
  }) => {
    const token = await getToken()
    if (!token) throw new Error("No token found")
    const response = await customAxios.put(
      `/booking/validation?bookingIds=${bookingIds.join(",")}`,
      {},
      { headers: { Authorization: token } }
    )
    return response.data
  }

  return useMutation({
    mutationFn: bookingValidation,
    onMutate: async ({ bookingIds }) => {
      await queryClient.cancelQueries({ queryKey: ["booking"] })
      const previousBookings = queryClient.getQueryData<BookingTable[]>([
        "booking",
      ])
      queryClient.setQueryData<BookingTable[]>(["booking"], (old) => {
        if (!old) return []
        return old.map((b) =>
          bookingIds.includes(b.bookingId)
            ? ({ ...b, state: "validated" } as BookingTable)
            : b
        )
      })
      return { previousBookings }
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previousBookings) {
        queryClient.setQueryData(["booking"], ctx.previousBookings)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["booking"] })
    },
  })
}

export const useBookingAbsentMutation = () => {
  const queryClient = useQueryClient()

  const bookingAbsent = async ({ bookingIds }: { bookingIds: string[] }) => {
    const token = await getToken()
    if (!token) throw new Error("No token found")
    const response = await customAxios.put(
      `/booking/absent?bookingIds=${bookingIds.join(",")}`,
      {},
      { headers: { Authorization: token } }
    )
    return response.data
  }

  return useMutation({
    mutationFn: bookingAbsent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking"] })
    },
  })
}

/**
 * Hook pour créer un booking immédiatement avant le paiement
 * Utilisé pour walk-in et réservations
 */
export const useCreateBookingForPaymentMutation = () => {
  const queryClient = useQueryClient()

  const createBooking = async (
    bookingData: CreateBookingForPaymentInput
  ): Promise<CreateBookingForPaymentOutput> => {
    const token = await getToken()
    if (!token) {
      throw new Error("No token found")
    }

    const response = await customAxios.post(
      "/booking/create-for-payment",
      bookingData,
      {
        headers: {
          Authorization: token,
        },
      }
    )

    return response.data as CreateBookingForPaymentOutput
  }

  return useMutation({
    mutationFn: createBooking,
    onSuccess: () => {
      // Invalider la liste des bookings pour rafraîchir
      queryClient.invalidateQueries({ queryKey: ["booking"] })
    },
    onError: (error) => {
      console.error("[CreateBookingForPayment] Error:", error)
    },
  })
}
